import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { passwordSchema } from '@brechoflow/contracts';
import { z } from 'zod';
import { toast } from '@/components/ui/toaster';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/empty-state';
import { FormField } from '@/components/ui/form-field';
import { PasswordInput } from '@/components/ui/password-input';
import { LinkBreak } from '@phosphor-icons/react';
import { applyServerErrors, contractResolver } from '@/lib/forms';
import { authApi } from '../auth-api';

const schema = z
  .object({ password: passwordSchema, confirm: z.string() })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'As senhas não conferem' });
type Values = z.input<typeof schema>;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Values, unknown, z.output<typeof schema>>({ resolver: contractResolver(schema), defaultValues: { password: '', confirm: '' } });

  if (token.length < 20) {
    return (
      <EmptyState
        icon={LinkBreak}
        title="Link de recuperação inválido"
        description="O link está incompleto ou expirou. Peça um novo para continuar."
        action={
          <Button asChild>
            <Link to="/esqueci-senha">Pedir novo link</Link>
          </Button>
        }
      />
    );
  }

  const onSubmit = handleSubmit(async ({ password }) => {
    setFormError(null);
    try {
      await authApi.resetPassword(token, password);
      toast.success('Senha alterada. Entre com a nova senha.');
      navigate('/entrar', { replace: true });
    } catch (e) {
      setFormError(applyServerErrors(e, setError));
    }
  });

  return (
    <>
      <h1 className="font-display text-3xl font-semibold">Nova senha</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">Escolha uma senha que você ainda não usa em outros lugares.</p>
      <form onSubmit={onSubmit} noValidate className="mt-7 flex flex-col gap-4">
        {formError && (
          <p role="alert" className="rounded-control bg-destructive-soft px-3 py-2.5 text-sm text-destructive">
            {formError}
          </p>
        )}
        <FormField label="Nova senha" hint="Mínimo de 8 caracteres, com letra e número." error={errors.password?.message}>
          {(a) => <PasswordInput autoComplete="new-password" autoFocus {...a} {...register('password')} />}
        </FormField>
        <FormField label="Repita a nova senha" error={errors.confirm?.message}>
          {(a) => <PasswordInput autoComplete="new-password" {...a} {...register('confirm')} />}
        </FormField>
        <Button type="submit" size="lg" loading={isSubmitting}>
          Salvar nova senha
        </Button>
      </form>
    </>
  );
}
